import React, { ReactNode } from 'react';
import { Flex } from 'app/components/common/Flex';
import { Collapse, Modal } from 'antd';
import {
    CustomLayerUtils,
    MapLayer,
    MapLayerUtils,
    MapObjectType,
    ObjectToDisplay,
    WeatherLayerUtils,
} from 'app/components/dashboard/network/map/Map';
import { MapTrafficVolumeCacheType } from 'app/components/dashboard/network/map/MapTrafficVolumeCache';
import { FlightLevelBand, TimePeriod } from 'app/model/CORE';
import { Rnd } from 'react-rnd';
import './MapLayer.css';
import Utils from 'app/common/util/Utils';
import memoizeOne from 'memoize-one';
import LayerSelectionEntry from 'app/components/dashboard/network/map/LayerSelectionEntry';

const { Panel } = Collapse;

interface MapLayersMenuPopupProps {
    layers: Array<MapLayer>;
    timePeriod: TimePeriod;
    flightLevelBand: FlightLevelBand;
    visible: boolean;
    top?: number | string;
    left?: number | string;

    onClose(): void;

    mapId: number;
}

interface MapLayersMenuPopupState {
    activePanels: Array<string>;
}

const NO_GROUP = '';

export class MapLayersMenuPopup extends React.PureComponent<MapLayersMenuPopupProps, MapLayersMenuPopupState> {
    componentDidMount(): void {
        this.setState({
            activePanels: [
                ...this.props.layers.map(layer => String(layer.id)),
                CustomLayerUtils.GROUP_LAYER,
                WeatherLayerUtils.GROUP_LAYER,
            ],
        });
    }

    render() {
        return (
            <Modal
                key="map-layers-menu-popup"
                mask={true}
                open={this.props.visible}
                width={'25em'}
                maskClosable={true}
                maskStyle={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
                modalRender={(modal: ReactNode) => (
                    <Rnd
                        dragHandleClassName="modal-title"
                        style={{ position: 'relative' }}
                        size={{ width: '100%', height: '100%' }}>
                        {modal}
                    </Rnd>
                )}
                centered={Utils.isNotExists(this.props.top) || Utils.isNotExists(this.props.left)}
                closable={true}
                forceRender
                footer={null}
                onCancel={this.props.onClose}
                style={this.getStyle()}
                // style={{margin: 'unset'}}
            >
                <Flex direction="vertical" className="settings-content" style={{ width: '25em', paddingTop: '1.5em' }}>
                    <Collapse
                        activeKey={this.state?.activePanels}
                        onChange={this.handleActivePanelChange}
                        style={{ padding: '1.5em' }}
                        collapsible="icon">
                        {this.renderLayers()}
                    </Collapse>
                </Flex>
            </Modal>
        );
    }

    renderLayers() {
        const groupedLayers = this.getGroupedLayers(this.props.layers);
        return Object.keys(groupedLayers).map(groupName => {
            if (groupName === NO_GROUP) {
                const individualLayers = groupedLayers[NO_GROUP];
                return individualLayers.map(layer => {
                    return (
                        <LayerSelectionEntry
                            key={layer.id}
                            header={layer.label}
                            timePeriod={this.props.timePeriod}
                            flightLevelBand={this.props.flightLevelBand}
                            mapId={this.props.mapId}
                            {...layer}
                        />
                    );
                });
            } else {
                const layerIds = groupedLayers[groupName].map(layer => layer.id);
                return (
                    <Panel key={groupName} header={groupName} className="groupedLayer">
                        <Collapse activeKey={layerIds}>
                            {groupedLayers[groupName].map(layer => {
                                return (
                                    <LayerSelectionEntry
                                        key={layer.id}
                                        header={layer.label}
                                        timePeriod={this.props.timePeriod}
                                        flightLevelBand={this.props.flightLevelBand}
                                        showArrow={false}
                                        mapId={this.props.mapId}
                                        {...layer}
                                    />
                                );
                            })}
                        </Collapse>
                    </Panel>
                );
            }
        });
    }

    //to avoid rerenders everytime the cache is updated but has nothing new to the give component
    getLayerCache = memoizeOne(
        (cache: MapTrafficVolumeCacheType, objectsToDisplay: Array<ObjectToDisplay>): MapTrafficVolumeCacheType => {
            const objectsToDisplayTvIds: Array<string> = objectsToDisplay
                .map(objToDisplay => {
                    if (objToDisplay.type === MapObjectType.TV) {
                        return MapLayerUtils.EXTENDED_MONITORED_TV_TV_ID_PROVIDER.getKey(objToDisplay.object);
                    } else if (objToDisplay.type === MapObjectType.MEASURE) {
                        return MapLayerUtils.MONITORED_MEASURE_TV_ID_PROVIDER.getKey(objToDisplay.object);
                    } else return null;
                })
                .filter(tvId => Utils.isExists(tvId));

            return objectsToDisplayTvIds.reduce((result: MapTrafficVolumeCacheType, tvId: string) => {
                if (cache[tvId]) {
                    result[tvId] = cache[tvId];
                }
                return result;
            }, {});
        },
    );

    getGroupedLayers = memoizeOne((layers: Array<MapLayer>): { [key: string]: Array<MapLayer> } => {
        return layers.reduce((result: { [key: string]: Array<MapLayer> }, obj: MapLayer) => {
            const group: string = Utils.isExists(obj.group) ? obj.group : NO_GROUP;
            if (Utils.isNotExists(result[group])) {
                result[group] = [];
            }
            result[group].push(obj);
            return result;
        }, {});
    });

    getStyle = () => {
        let style = { margin: 'unset' };
        if (Utils.isExists(this.props.top) && Utils.isExists(this.props.left)) {
            style = Object.assign(style, { top: this.props.top, left: this.props.left });
        }
        return style;
    };

    //handlers
    handleActivePanelChange = value => {
        this.setState({
            activePanels: value,
        });
    };
}
